import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Avatar, Breadcrumb, Dropdown, Tooltip, type MenuProps } from 'antd'
import {
  BellOutlined,
  CheckOutlined,
  CommentOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  ShopOutlined,
  SunOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { useAuth } from '@/app/AuthProvider'
import { useDesign } from '@/app/DesignProvider'
import { useStore } from '@/app/StoreProvider'
import { fetchStoreTerminals } from '@/features/cash/api'
import type { CashTerminal } from '@/features/cash/types'
import { WeatherWidget } from '@/features/weather/WeatherWidget'
import { HeaderClock } from '@/layout/HeaderClock'
import { HeaderDate } from '@/layout/HeaderDate'
import { resolveNavLabel } from '@/shared/permissions'
import styles from './TopBar.module.css'

interface Props {
  onOpenStorePicker: () => void
  onOpenCalendar: () => void
  onOpenChat?: () => void
  chatUnread?: number
  onOpenUpdates?: () => void
  updatesUnread?: number
}

function sortByNum(rows: CashTerminal[]) {
  return [...rows].sort((a, b) => {
    const an = Number(a.num)
    const bn = Number(b.num)
    if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return an - bn
    return String(a.num ?? a.name ?? a.id).localeCompare(String(b.num ?? b.name ?? b.id), 'ru')
  })
}

function CashBreadcrumbDropdown({
  storeId,
  currentId,
}: {
  storeId: string | null
  currentId: string | null
}) {
  const navigate = useNavigate()
  const [terminals, setTerminals] = useState<CashTerminal[]>([])
  const [loadedFor, setLoadedFor] = useState<string | null>(null)

  useEffect(() => {
    if (!storeId) {
      setTerminals([])
      setLoadedFor(null)
      return
    }
    let cancelled = false
    void fetchStoreTerminals(storeId, false)
      .then((list) => {
        if (cancelled) return
        setTerminals(sortByNum(list))
        setLoadedFor(storeId)
      })
      .catch(() => {
        if (cancelled) return
        setTerminals([])
        setLoadedFor(storeId)
      })
    return () => {
      cancelled = true
    }
  }, [storeId])

  const items: MenuProps['items'] = useMemo(() => {
    if (loadedFor !== storeId) {
      return [{ key: 'loading', label: 'Загрузка…', disabled: true }]
    }
    if (!terminals.length) {
      return [{ key: 'empty', label: 'Нет касс', disabled: true }]
    }
    return terminals.map((t) => {
      const id = String(t.id)
      const active = currentId === id
      const label = `Касса ${t.num ?? t.name ?? id}`
      return {
        key: id,
        label: (
          <span className={styles.cashMenuItem}>
            <span className={styles.cashMenuLabel}>{label}</span>
            {t.ip ? <span className={styles.cashMenuMeta}>{t.ip}</span> : null}
            {active ? <CheckOutlined className={styles.cashMenuCheck} /> : null}
          </span>
        ),
        onClick: () => {
          if (!active) navigate(`/cash/${id}`)
        },
      }
    })
  }, [terminals, loadedFor, storeId, currentId, navigate])

  return (
    <Dropdown
      trigger={['hover']}
      mouseEnterDelay={0.15}
      mouseLeaveDelay={0.2}
      menu={{
        items,
        selectedKeys: currentId ? [currentId] : [],
        className: styles.cashMenu,
      }}
    >
      <Link to="/cash" className={styles.cashCrumb}>
        Кассы
      </Link>
    </Dropdown>
  )
}

export function TopBar({
  onOpenStorePicker,
  onOpenCalendar,
  onOpenChat,
  chatUnread = 0,
  onOpenUpdates,
  updatesUnread = 0,
}: Props) {
  const { user, logout } = useAuth()
  const { prefs, toggleTheme, sidebarCollapsed, setSidebarCollapsed } = useDesign()
  const { selectedStore, storeId, loading: storesLoading } = useStore()
  const location = useLocation()
  const navigate = useNavigate()

  const cashDetailMatch = location.pathname.match(/^\/cash\/([^/]+)/)
  const cashDetailRaw = cashDetailMatch?.[1] || null
  const cashDetailId =
    cashDetailRaw && cashDetailRaw !== 'incidents' ? cashDetailRaw : null

  const crumbs: { title: ReactNode }[] = [{ title: <Link to="/">KIT Support</Link> }]
  if (location.pathname !== '/') {
    const parts = location.pathname.split('/').filter(Boolean)
    let acc = ''
    parts.forEach((part, idx) => {
      acc += `/${part}`
      const isLast = idx === parts.length - 1
      const label = resolveNavLabel(acc)

      if (acc === '/cash' && cashDetailId) {
        crumbs.push({
          title: <CashBreadcrumbDropdown storeId={storeId} currentId={cashDetailId} />,
        })
        return
      }

      crumbs.push({
        title: isLast ? label : <Link to={acc}>{label}</Link>,
      })
    })
  }

  const storeLabel = storesLoading
    ? 'Загрузка…'
    : selectedStore?.name || (storeId ? `Магазин ${storeId}` : 'Выберите магазин')

  return (
    <header className={styles.topbar} role="banner">
      <div className={styles.left}>
        <Tooltip title={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}>
          <button
            type="button"
            className={styles.iconBtn}
            aria-label={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
        </Tooltip>
        <Breadcrumb items={crumbs} className={styles.breadcrumb} />
      </div>

      <button
        type="button"
        className={styles.search}
        onClick={onOpenStorePicker}
        aria-label="Выбор магазина"
        title={storeLabel}
      >
        <ShopOutlined />
        <span className={styles.searchLabel}>{storeLabel}</span>
        <kbd className={styles.kbd}>Ctrl+K</kbd>
      </button>

      <div className={styles.right}>
        <HeaderDate onClick={onOpenCalendar} />
        <HeaderClock />
        <WeatherWidget />
        <Tooltip title={prefs.theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}>
          <button
            type="button"
            className={styles.iconBtn}
            aria-label="Переключить тему"
            onClick={toggleTheme}
          >
            {prefs.theme === 'dark' ? <SunOutlined /> : <MoonOutlined />}
          </button>
        </Tooltip>
        {onOpenUpdates ? (
          <Tooltip title="Что нового">
            <button
              type="button"
              className={styles.iconBtn}
              aria-label="Что нового"
              onClick={onOpenUpdates}
            >
              <BellOutlined />
              {updatesUnread > 0 ? (
                <span className={styles.badgeCount}>
                  {updatesUnread > 9 ? '9+' : updatesUnread}
                </span>
              ) : null}
            </button>
          </Tooltip>
        ) : null}
        {onOpenChat ? (
          <Tooltip title="Общий чат">
            <button type="button" className={styles.iconBtn} aria-label="Чат" onClick={onOpenChat}>
              <CommentOutlined />
              {chatUnread > 0 ? (
                <span className={styles.badge}>{chatUnread > 99 ? '99+' : chatUnread}</span>
              ) : null}
            </button>
          </Tooltip>
        ) : null}
        <Dropdown
          menu={{
            items: [
              {
                key: 'profile',
                icon: <UserOutlined />,
                label: user?.name || 'Профиль',
                onClick: () => navigate('/profile'),
              },
              {
                key: 'help',
                icon: <QuestionCircleOutlined />,
                label: 'О системе',
                onClick: () => navigate('/preview'),
              },
              {
                key: 'settings',
                icon: <SettingOutlined />,
                label: 'Настройки',
                onClick: () => navigate('/settings'),
              },
              { type: 'divider' },
              {
                key: 'logout',
                icon: <LogoutOutlined />,
                danger: true,
                label: 'Выйти',
                onClick: async () => {
                  await logout()
                  navigate('/login', { replace: true })
                },
              },
            ],
          }}
        >
          <button type="button" className={styles.avatarBtn} aria-label="Меню пользователя">
            <Avatar
              size={28}
              src={user?.avatar_url || undefined}
              icon={<UserOutlined />}
              style={{ background: 'var(--bg-hover)', color: 'var(--text-secondary)' }}
            />
          </button>
        </Dropdown>
      </div>
    </header>
  )
}
